import axios, { AxiosResponse } from "axios";

import { toApiError } from "@/lib/error";

import { TwoFactorMethod } from "./twoFactor";

export const getAvailableCredentials = async (
  email: string,
  redirectUrl: string | undefined | null,
  {
    onPasswordNeeded,
    onSsoUrl,
    onSsoOnly,
    onFailed,
  }: {
    onPasswordNeeded(): void;
    onSsoUrl(url: string): void;
    /** The account signs in only through its IdP: the caller navigates to `url` */
    onSsoOnly(url: string): void;
    onFailed?(): void;
  },
) => {
  let response: AxiosResponse;
  try {
    response = await axios.get(`/api/credentials/chooser`, {
      params: {
        login: email,
        redirectUrl,
      },
    });
  } catch (e) {
    console.error("Failed to fetch available credentials", e);
    onFailed?.();
    return;
  }

  if (response.data.authorizationMethodType === "SSO") {
    onSsoOnly(response.data.ssoUrl);
  } else if (response.data.authorizationMethodType === "PASSWORD") {
    onPasswordNeeded();
  } else if (response.data.authorizationMethodType === "PASSWORD_SSO") {
    onPasswordNeeded();
    onSsoUrl(response.data.ssoUrl);
  } else {
    onFailed?.();
  }
};

/** `OK` carries where the signed-in user goes next; the caller navigates there */
type LoginResult =
  | { status: "OK"; location: string }
  | { status: "MFA_REQUIRED"; method: TwoFactorMethod; emailHint: string | null };

/** The login gate refused the attempt before checking the password; `Retry-After` says for how long. */
export class LoginThrottledError extends Error {
  constructor(readonly retryAfterSeconds: number | null) {
    super("LOGIN_THROTTLED");
    this.name = "LoginThrottledError";
  }
}

// A 401 names the refusal in `code`; its `reason` is the server's own wording and never decides
// anything here. A code this page does not know is a failure like any other.
const REFUSAL_CODES = ["BAD_CREDENTIALS", "ACCOUNT_LOCKED", "ACCOUNT_DEACTIVATED"];

const refusalCode = (code: unknown) =>
  typeof code === "string" && REFUSAL_CODES.includes(code) ? code : "LOGIN_FAILED";

export const sendLoginForm = async (
  email: string,
  pass: string,
  redirectUrl?: string | null,
): Promise<LoginResult> => {
  const form = new FormData();
  form.append("username", email);
  form.append("password", pass);
  if (redirectUrl != null) {
    form.append("redirect_url", redirectUrl);
  }
  try {
    const response = await axios.post("/login", form);

    if (response.data?.mfaRequired) {
      return {
        status: "MFA_REQUIRED",
        method: response.data.method ?? "TOTP",
        emailHint: response.data.emailHint ?? null,
      };
    }
    return { status: "OK", location: response.headers["location"] };
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.status === 401) {
      throw new Error(refusalCode(e.response.data?.code));
    }
    if (axios.isAxiosError(e) && e.response?.status === 429) {
      const retryAfter = Number.parseInt(e.response.headers?.["retry-after"], 10);
      throw new LoginThrottledError(Number.isNaN(retryAfter) ? null : retryAfter);
    }
    throw new Error("LOGIN_FAILED");
  }
};

export interface TwoFactorVerified {
  location: string;
  factor: string;
  backupCodesRemaining: number;
}

export const verifyTwoFactorCode = async (
  code: string,
  redirectUrl?: string | null,
): Promise<TwoFactorVerified> => {
  // The server reads `redirect_url` with `request.getParameter`, which never sees a JSON body, so it
  // travels on the query string here - the same place `getInvitationStatus` puts it.
  const url =
    "/api/credentials/2fa/verify" +
    (redirectUrl != null ? `?redirect_url=${encodeURIComponent(redirectUrl)}` : "");
  try {
    const response = await axios.post(url, { code });
    return {
      location: response.headers["location"],
      factor: response.data?.factor,
      backupCodesRemaining: response.data?.backupCodesRemaining,
    };
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.status === 401) {
      throw new Error("MFA_FAILED");
    }
    throw new Error("Verification failed");
  }
};

/** Another e-mailed code for the open challenge. The server bounds this at three per challenge. */
export const resendTwoFactorCode = async (): Promise<void> => {
  try {
    await axios.post("/api/credentials/2fa/resend");
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.status === 429) {
      throw new Error("RESEND_LIMIT");
    }
    // A 401 means the challenge expired while the code step was still on screen. Retrying the
    // send cannot help - there is nothing left to send a code for - so it needs its own message.
    if (axios.isAxiosError(e) && e.response?.status === 401) {
      throw new Error("RESEND_EXPIRED");
    }
    throw new Error("RESEND_FAILED");
  }
};

export const sendPasswordResetRequest = async (email: string, redirectUrl?: string) => {
  await axios.post(`/api/credentials/password/reset`, {
    email: email,
    redirect_url: redirectUrl,
  });
};

/**
 * Finishes a password reset or an invitation. An account with an authenticator app answers a reset
 * without `code` with `SECOND_FACTOR_REQUIRED`; the caller then asks for the code and calls again.
 */
export const sendPasswordSetRequest = async (
  token: string,
  invitation: string,
  password: string,
  code?: string,
) => {
  const params: {
    newPassword: string;
    passwordResetToken?: string;
    invitationToken?: string;
    code?: string;
  } = {
    newPassword: password,
  };
  if (typeof token !== "undefined") {
    params.passwordResetToken = token;
  }
  if (typeof invitation !== "undefined") {
    params.invitationToken = invitation;
  }
  if (code) {
    params.code = code;
  }

  try {
    await axios.put(`/api/credentials/password/set`, params);
  } catch (e) {
    throw toApiError(e, "Setting the password failed. Please try again.");
  }
};

type InvitationStatusDto =
  | {
      status: "VALID" | "INVALID" | "PASSWORD_SET" | "ALREADY_LOGGED_IN" | "EXPIRED";
      redirectUrl: string | null;
    }
  | {
      status: "SSO";
      redirectUrl: string;
    };

export const getInvitationStatus = async (invitationId: string, redirectUrl?: string) => {
  let url = `/api/invitations/` + invitationId + "/status";
  if (typeof redirectUrl !== "undefined") {
    url += "?redirect_url=" + redirectUrl;
  }

  const response = await axios.get<InvitationStatusDto>(url);

  return response.data;
};

export const sendLogoutForm = async () => {
  const logoutForm = document.createElement("form");
  document.body.appendChild(logoutForm);

  logoutForm.action = `/logout?redirect_url=${encodeURIComponent(window.location.href)}`;
  logoutForm.method = "POST";
  logoutForm.submit();

  return new Promise(() => {});
};
